<template>
	<form :action="actionUrl" :method="method" @submit.prevent="nativeValidationSubmit" novalidate>
		<slot/>
	</form>
</template>

<script setup lang="ts">
import {SubmittableComponentEmits, type SubmittableComponentProps, useSubmittableComponent} from "../../composables/useSubmittableComponent";
import type {Ref} from "vue";
import {provide, ref, watch} from "vue";
import {isObject} from "@spoferan/spoferan-ts-core";
import {formInputInjection} from "../../types/keys";
import {useI18n, useNuxtApp} from "#imports";

type FormInputInject = {
	id: string,
	name: string | undefined,
	validate: (forceErrorDisplay: boolean) => boolean,
	reset: () => void,
	clearValidation: () => void,
	setValidationError: (errors: {[key: string]: string}) => void,
}

const props = defineProps<SubmittableComponentProps & {
	// Whether all input field inputs are valid and the form can be submitted
	valid?: boolean,

	// Whether to reset the data after the submission has been successfully processed
	reset?: boolean,

	// Whether to show an error alert on errors for GET-requests
	// For other requests an error alert is always shown for errors
	withError?: boolean,
}>();

const emit = defineEmits([...SubmittableComponentEmits, 'reset', 'update:valid']);

const {$notify} = useNuxtApp();
const {t} = useI18n();

const {submit, actionUrl, isLoading} = useSubmittableComponent(props, emit, {
	successHandler: () => {
		if (props.reset) {
			resetForm()
		}
		clearValidation()
	},
	errorHandler: (status, error) => {
		if (status === 422) {
			handleFailedValidation(error.errors)
		}
	}
});

const errorBag = ref({});
const isValid = ref(props.valid ?? true);
watch(isValid, (newValue) => {
	emit('update:valid', newValue);
});
watch(() => props.valid, (newValue) => {
	if (newValue !== isValid.value) {
		isValid.value = newValue;
	}
});

// The child input components of the form
const inputs: Ref<FormInputInject[]> = ref([]);

/**
 * Calling this function prevents a deep watcher.
 */
function refreshValidState() {
	isValid.value = Object.values(errorBag.value).filter(value => value !== null).length === 0;
}

function addInput(input: FormInputInject) {
	inputs.value.push(input);
}

function removeInput(id) {
	removeInputError(id);
	inputs.value = inputs.value.filter(i => i.id !== id)
}

function validate(forceErrorDisplay = false) {
	if (inputs.value.filter(input => !input.validate(forceErrorDisplay)).length === 0) {
		return true;
	}

	if (forceErrorDisplay) {
		notifyErrors();
	}

	return false;
}

async function nativeValidationSubmit() {
	try {
		await validationSubmit();
	} catch (e) {
		// Do nothing as throwing errors would cause fatal errors with full screen error page on native event handlers
	}
}

async function validationSubmit() {
	if (validate(true)) {
		return submit()
	}
	return new Promise((resolve, reject) => reject());
}

function resetForm() {
	inputs.value.forEach(input => {
		input.reset()
	})
	emit('reset');
}

function clearValidation() {
	inputs.value.forEach(input => {
		input.clearValidation()
	})
}

function addInputError(id: string, {label, name, message}) {
	errorBag.value[id] = {
		label: label,
		name: name,
		message: message
	};
	refreshValidState();
}

function removeInputError(id: string) {
	delete errorBag.value[id];
	refreshValidState();
}

/**
 * Displays a notification with the current error messages if any exist.
 */
function notifyErrors() {
	if (isValid.value) {
		return;
	}
	const errors = Object.values(errorBag.value).filter(value => value !== null);
	if (!errors.length) {
		return;
	}
	const errorMessage = errors.map(error => error.label ? `<b>${error.label}:</b> ${error.message}` : error.message).join('<br>')
	$notify('error', errorMessage, t('validation.check_inputs'))
}

function handleFailedValidation(failedFields) {
	if (failedFields && Object.keys(failedFields).length) {

		// Add the server errors to the input fields
		Object.keys(failedFields).forEach(fieldName => {
			const input = inputs.value.find(input => input.name === fieldName);
			if (input) {
				const failedValidation = failedFields[fieldName][0]
				input.setValidationError({
					server: isObject(failedValidation) ? failedValidation.message : failedValidation
				});
			}
		})
	}
}

provide(formInputInjection, {
	addInput,
	removeInput,
	addInputError,
	removeInputError,
});

defineExpose({
  validationSubmit,
  validate,
	clearValidation,
});

</script>